import {
  FIRST_ARRAY_INDEX,
  EMPTY_ARRAY_LENGTH,
  surveyQuestionInsertOffsets,
} from "../../../constants";

/**
 * Update a question in the list by questionOrder.
 */
export const updateQuestionByOrder = (questionList, questionOrder, updater) =>
  (questionList || []).map((obj) =>
    obj.questionOrder === questionOrder ? updater(obj) : obj,
  );

/**
 * Update a question in the list by questionNo.
 */
export const updateQuestionByNo = (questionList, questionNo, updater) =>
  (questionList || []).map((obj) =>
    obj.questionNo === questionNo ? updater(obj) : obj,
  );

/**
 * Update a question matched by both questionId and questionNo.
 */
export const updateQuestionByIdAndNo = (
  questionList,
  questionId,
  questionNo,
  updater,
) =>
  (questionList || []).map((obj) =>
    obj.questionId === questionId && obj.questionNo === questionNo
      ? updater(obj)
      : obj,
  );

/**
 * After archiving/removing a question, mark later items edited and re-number orders.
 */
export const archiveAndReorderQuestions = (
  questionList,
  questionOrder,
  questionId,
) =>
  (questionList || [])
    .map((ques) => ({
      ...ques,
      ...(Number(ques.questionOrder) > Number(questionOrder) && {
        isEdit: !ques.isNew,
      }),
    }))
    .filter((q) => q.id !== questionId)
    .map((ques, index) => ({
      ...ques,
      questionOrder: index + 1,
    }));

/**
 * Insert a new question before trailing NPS / sentiment questions.
 */
export const insertQuestionBeforeTrailingNps = (
  questionList,
  newQuestion,
  isNpsFirstFullyActivated,
) => {
  const insertOffset = isNpsFirstFullyActivated
    ? surveyQuestionInsertOffsets.NPS_FIRST_FULL
    : surveyQuestionInsertOffsets.DEFAULT;
  const insertPosition = questionList.length - insertOffset;

  return [
    ...questionList.slice(FIRST_ARRAY_INDEX, insertPosition),
    newQuestion,
    ...questionList.slice(insertPosition),
  ];
};

/**
 * Reorder questions for save when NPS First is fully activated.
 * Sentiment → 2nd last; last question stays last; others sequential from 1.
 */
export const reorderQuestionsForNpsFirstSave = (originalQuestions = []) => {
  let currentOrder = 1;
  const lastIndex = originalQuestions.length - 1;

  return originalQuestions.map((q, index) => {
    if (q.isSentimentQuestion) {
      return {
        ...q,
        questionOrder: lastIndex, // 2nd last in 1-based numbering when length >= 2
      };
    }
    if (index === lastIndex) {
      return {
        ...q,
        questionOrder: originalQuestions.length,
      };
    }
    return {
      ...q,
      questionOrder: currentOrder++,
    };
  });
};

/**
 * Move sentiment question to the start for display when NPS First is fully on.
 */
export const moveSentimentQuestionToStart = (questions = []) => {
  let reorderedQuestions = [...questions];
  const sentimentIndex = reorderedQuestions.findIndex(
    (q) => q.isSentimentQuestion,
  );

  if (sentimentIndex > FIRST_ARRAY_INDEX) {
    const [sentimentQuestion] = reorderedQuestions.splice(sentimentIndex, 1);
    reorderedQuestions.unshift(sentimentQuestion);
    reorderedQuestions = reorderedQuestions.map((q, index) => ({
      ...q,
      questionOrder: index + 1,
    }));
  }

  return reorderedQuestions;
};

/**
 * Drag-and-drop reorder helper — updates questionOrder / isEdit.
 */
export const reorderDraggedQuestions = (listOfQuestions, startIndex, endIndex) => {
  const result = [...listOfQuestions];
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, EMPTY_ARRAY_LENGTH, removed);

  return result.map((q, index) => ({
    ...q,
    questionOrder: index + 1,
    isEdit: !q.isNew,
  }));
};

/**
 * Compare current list order to expected order by id.
 */
export const areQuestionOrdersEqual = (questionList, expectedOrderedQuestions) =>
  questionList.length === expectedOrderedQuestions.length &&
  questionList.every((currentQuestion, index) => {
    const expectedQuestion = expectedOrderedQuestions[index];
    return expectedQuestion && currentQuestion.id === expectedQuestion.id;
  });

export const hasMultipleQuestions = (questionsAnswerArray = []) =>
  questionsAnswerArray.length > EMPTY_ARRAY_LENGTH + 1;
